import { Table } from "@xola/ui-kit";
import clsx from "clsx";
import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "react-query";
import { SellerPaymentMethod } from "types";
import shallow from "zustand/shallow";
import { useSeller } from "../../../hooks/useAccount";
import { getBackofficePaymentMethods } from "../../../models/Payment";
import { getEarningReportGroupedTransactions, GroupedTransaction } from "../../../reports/EarningsReport";
import { useEarningsReportFilterStore } from "../../../stores/reports/earningsReportFilterStore";
import { Currency } from "../../Common/Currency";
import { TableSkeleton } from "../../Common/Skeleton/TableSkeleton";
import { ReservationistName } from "./ReservationistName";

export const GroupByReservationist = ({
    onChangeGroupByReservationist,
}: {
    onChangeGroupByReservationist: (value: boolean) => void;
}) => {
    const seller = useSeller();
    const [headerContentForReservationist, setHeaderContentForReservationist] = useState<string[]>();
    const paymentMethods = useMemo(() => getBackofficePaymentMethods(seller), [seller]);

    const [from, to] = useEarningsReportFilterStore((state) => [state.getFrom(), state.getTo()], shallow);
    const [filters, setFilters] = useEarningsReportFilterStore((state) => [state.filters, state.setFilters], shallow);

    const params = { seller, filters, from, to };
    const { data: groupedTransactions, isLoading } = useQuery(["earningsReportGropedTransactions", params], async () =>
        getEarningReportGroupedTransactions(params),
    );

    const handleOnNameClick = (id: string | null) => {
        if (id) {
            setFilters({ reservationist: { all: false, selected: [id] } });
            onChangeGroupByReservationist(false);
        }
    };

    useEffect(() => {
        const data: string[] = [];
        for (const method of paymentMethods) {
            if (filters.paymentMethods.all) {
                data.push(method.name);
            } else if (method.id && filters.paymentMethods.selected.includes(method.id)) {
                data.push(method.name);
            }
        }

        setHeaderContentForReservationist(data);
    }, [filters, paymentMethods]);

    if (!groupedTransactions || isLoading) {
        return <TableSkeleton rows={6} columns={6} />;
    }

    return (
        <Table>
            <Table.Head>
                <Table.Row>
                    <Table.Header>Name</Table.Header>
                    {headerContentForReservationist?.map((element: any) => {
                        return (
                            <Table.Header key={element} className="text-right">
                                {element}
                            </Table.Header>
                        );
                    })}
                    <Table.Header className="text-right">Total</Table.Header>
                </Table.Row>
            </Table.Head>
            <Table.Body isStriped>
                {groupedTransactions.map((transaction: GroupedTransaction) => (
                    <Table.Row key={transaction.id}>
                        <Table.Cell>
                            <ReservationistName
                                id={transaction.reservationist.id ?? transaction?.id ?? ""}
                                name={transaction.reservationist.name}
                                reservationistExists={Boolean(transaction.reservationist.id)}
                                onNameClick={handleOnNameClick}
                            />
                        </Table.Cell>
                        {paymentMethods.map((method: SellerPaymentMethod) => {
                            if (
                                method.id &&
                                !filters.paymentMethods.all &&
                                !filters.paymentMethods.selected.includes(method.id)
                            ) {
                                return null;
                            }

                            return (
                                <GroupReservationistCell
                                    key={method.id}
                                    cell={method?.id}
                                    amount={transaction.paymentMethods[method.id]}
                                    method={method.id}
                                />
                            );
                        })}
                        <Table.Cell className={clsx("text-right", transaction.total < 0 ? "text-red" : "")}>
                            <Currency>{transaction.total}</Currency>
                        </Table.Cell>
                    </Table.Row>
                ))}
            </Table.Body>
        </Table>
    );
};

interface GroupReservationistCellProps {
    cell?: string;
    amount: number;
    method: string;
}

const GroupReservationistCell = ({ cell, amount, method }: GroupReservationistCellProps) => {
    return (
        <Table.Cell className={clsx("text-right", amount < 0 && method === cell ? "text-red" : "")}>
            {cell && method === cell && amount && amount !== 0 ? <Currency>{amount}</Currency> : <>&mdash;</>}
        </Table.Cell>
    );
};
